/**
 * Табы языков над полями контента.
 *
 * Копия такого же компонента в кабинете партнёра: приложения собираются
 * отдельно, и общий пакет ради сорока строк разметки стоил бы дороже копии.
 *
 * Базовый язык — русский, он лежит в самих полях акции. Перевод живёт рядом,
 * в `i18n`, и пустое поле перевода означает «показывать русский текст»: мы
 * никогда не отдадим покупателю пустой заголовок ради принципа.
 *
 * Поэтому на вкладке перевода поля пустые, а не заполнены русским: иначе
 * партнёр не отличил бы «перевёл так же» от «не переводил вовсе».
 */
import { Badge } from '@allswipes/ui';

export const LANGS = [
  { code: 'ru', label: 'Русский' },
  { code: 'en', label: 'English' },
] as const;

export type ContentLang = (typeof LANGS)[number]['code'];

export type Translations = Record<string, Record<string, string>>;

export function LangTabs({
  lang,
  onChange,
  i18n,
}: {
  lang: ContentLang;
  onChange: (lang: ContentLang) => void;
  i18n?: Translations;
}) {
  return (
    <div className="langtabs">
      {LANGS.map((l) => {
        const filled = l.code === 'ru' ? 0 : Object.values(i18n?.[l.code] ?? {}).filter(Boolean).length;
        return (
          <button
            key={l.code}
            type="button"
            className={`langtab ${lang === l.code ? 'on' : ''}`}
            onClick={() => onChange(l.code)}
          >
            {l.label}
            {l.code !== 'ru' &&
              (filled > 0 ? <Badge tone="good">{filled}</Badge> : <Badge>нет перевода</Badge>)}
          </button>
        );
      })}
    </div>
  );
}

/** Значение поля на выбранном языке. */
export function tr(i18n: Translations | undefined, lang: ContentLang, field: string, base: string): string {
  return lang === 'ru' ? base : (i18n?.[lang]?.[field] ?? '');
}

/** Запись перевода: пустая строка стирает ключ, чтобы не копить мусор. */
export function setTr(
  i18n: Translations | undefined,
  lang: ContentLang,
  field: string,
  value: string,
): Translations {
  const next: Translations = { ...(i18n ?? {}) };
  const fields = { ...(next[lang] ?? {}) };
  if (value.trim()) fields[field] = value;
  else delete fields[field];
  if (Object.keys(fields).length > 0) next[lang] = fields;
  else delete next[lang];
  return next;
}
